<template>
  <div>
  <vx-card>
    <!-- Venue Selection Dropdown -->
    <!-- <div class="mb-6">
      <label class="text-sm font-medium text-gray-700 mb-2 block">Select Venue</label>
      <v-select
        v-model="selectedVenue"
        :options="venueOptions"
        :dir="$vs.rtl ? 'rtl' : 'ltr'"
        placeholder="Choose a venue..."
        class="w-full"
        :reduce="(option) => option.id"
        label="display_name"
      />
    </div> -->
    <!-- No Venues Message -->
    <div v-if="venueOptions.length === 0" class="text-center py-12">
      <div class="text-gray-500">
        <feather-icon icon="BuildingIcon" size="48" class="mx-auto mb-4 text-gray-300" />
        <h3 class="text-lg font-medium text-gray-700 mb-2">No Venues Available</h3>
        <p class="text-sm text-gray-500">There are no venues configured for your company yet.</p>
        <p class="text-xs text-gray-400 mt-1">Please add or contact your administrator to add venues.</p>
      </div>
    </div>

    <!-- All Venues QR Status Overview -->
    <div v-if="venueOptions.length > 0" class="mb-6">
      <vs-divider position="center">
        <h5>All Venues QR Code Status</h5>
      </vs-divider>
      
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div 
          v-for="venue in venueOptions" 
          :key="venue.id"
          class="p-4 border-2 rounded-lg cursor-pointer hover:shadow-lg transition-all duration-200"
          :class="getVenueTileClass(venue.id)"
          @click="openQRBookerManagement(venue.id)"
        >
          <div class="flex items-center justify-between mb-2 ">
            <div class="flex items-center ">
              <h6 
                class="font-medium text-sm"
                :class="{
                  'text-blue-700 font-bold': selectedVenue === venue.id,
                  'text-gray-800': selectedVenue !== venue.id
                }"
              >
                {{ venue.display_name }}
              </h6>
              <!-- Selection indicator -->
              <feather-icon 
                v-if="selectedVenue === venue.id"
               
                class="ml-2 text-blue-600"
                size="10"
              />
            </div>
            <div class="flex items-center">
              <feather-icon 
                :class="{
                  'text-green-500': venueQRStatus[venue.id],
                  'text-gray-400': !venueQRStatus[venue.id]
                }"
                size="16"
              />
            </div>
          </div>
          <p class="text-xs text-gray-600 mb-2">ID: {{ venue.id }}</p>
          <div class="flex items-center justify-between">
            <span 
              class="text-sm pr-2 py-1 rounded-full font-medium "
              :class="{
                'bg-blue-200 text-blue-800 border border-blue-300': selectedVenue === venue.id && venueQRStatus[venue.id],
                'bg-green-100 text-green-700': venueQRStatus[venue.id] && selectedVenue !== venue.id,
                'bg-gray-100 text-gray-600': !venueQRStatus[venue.id] && selectedVenue !== venue.id,
                'bg-red-100 text-red-700 border border-red-300': selectedVenue === venue.id && !venueQRStatus[venue.id]
              }"
            >
              {{ venueQRStatus[venue.id] ? 'QR Available' : 'No QR Code' }}
            </span>
            <vs-button 
              v-if="venueQRStatus[venue.id]"
              @click.stop="openQRBookerManagement(venue.id)"
              size="small"
              color="success"
              type="filled"
              icon-pack="feather"
              icon="icon-eye"
            >
              View QR
            </vs-button>
            <vs-button 
              v-else
              @click.stop="openQRBookerManagement(venue.id)"
              size="small"
              color="primary"
              type="filled"
              icon-pack="feather"
              icon="icon-plus"
            >
              Manage QR
            </vs-button>
          </div>
        </div>
      </div>
    </div>

  </vx-card>

  <!-- Portal Settings Modal -->
  <vs-popup 
    :active.sync="showPortalModal" 
    title="Portal Settings" 
    class="portal-settings-modal"
  >
    <div v-if="selectedVenue" class="p-6">
      <p class="text-sm text-gray-600 mb-6">Configure the booking portal settings for {{ getSelectedVenueName() }}</p>
      
      <div class="space-y-6">
        <div class="flex flex-col md:flex-row md:space-x-8">
          <!-- LEFT: General Settings -->
          <div class="md:w-1/2 space-y-4">
            <h5 class="text-md font-semibold mb-4">General Settings</h5>

            <div class="flex justify-between items-center">
              <label class="vs-input--label">Ask Customer Name</label>
              <vx-tooltip text="Enable to request the customer's name">
                <vs-switch v-model="portalConfig.name" />
              </vx-tooltip>
            </div>

            <div class="flex justify-between items-center">
              <label class="vs-input--label">Ask Destination</label>
              <vx-tooltip text="Enable to request the destination address">
                <vs-switch v-model="portalConfig.destination" />
              </vx-tooltip>
            </div>

            <div class="flex justify-between items-center">
              <label class="vs-input--label">Ask Phone Number</label>
              <vx-tooltip text="Enable to request the customer's phone number">
                <vs-switch v-model="portalConfig.phone" />
              </vx-tooltip>
            </div>

            <div class="flex justify-between items-center">
              <label class="vs-input--label">Allow Schedule Booking</label>
              <vx-tooltip text="Enable customers to schedule bookings in advance">
                <vs-switch v-model="portalConfig.schedule_booking" />
              </vx-tooltip>
            </div>

            <div class="flex justify-between items-center">
              <label class="vs-input--label">Allow Additional Notes</label>
              <vx-tooltip text="Enable a text box for customers to leave notes">
                <vs-switch v-model="portalConfig.allow_notes" />
              </vx-tooltip>
            </div>
          </div>

          <!-- RIGHT: Vehicle Capabilities -->
          <div class="md:w-1/2">
            <h5 class="text-md font-semibold mb-4">Vehicle Capabilities</h5>

            <v-select
              class="w-full"
              :options="vehicleOptions"
              v-model="selectedCapabilities"
              :multiple="true"
              label="name"
              :track-by="vehicleTrackBy"
              placeholder="Select one or more vehicle types"
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end gap-3 pt-4 border-t">
          <vs-button 
            @click="closePortalModal" 
            color="secondary"
            type="filled"
            size="small"
            icon-pack="feather"
            icon="icon-x"
          >
            Cancel
          </vs-button>
          <vs-button 
            @click="updatePortalSettings" 
            :disabled="isUpdatingPortal"
            color="primary"
            type="filled"
            size="small"
            icon-pack="feather"
            icon="icon-settings"
          >
            {{ isUpdatingPortal ? 'Updating...' : 'Update Settings' }}
          </vs-button>
        </div>
      </div>
    </div>
  </vs-popup>

  <!-- QR Booker Management Modal -->
  <vs-popup 
    :active.sync="showQRBookerModal" 
    title="QR Booker Management" 
    class="qr-booker-management-modal"
    size="large"
  >
    <div v-if="selectedVenue" class="p-6">
      <div class="mb-6 text-center">
        <h4 class="text-lg font-semibold text-gray-800 mb-2">Managing QR Code for: {{ getSelectedVenueName() }}</h4>
        <p class="text-sm text-gray-600">Generate, manage, and configure QR codes for this venue</p>
      </div>
      
      <!-- Vertical Layout -->
      <div class="space-y-6">
        <!-- QR Code Display -->
        <div class="bg-white border border-gray-200 rounded-lg p-6 flex flex-col">
          
          <div v-if="selectedVenueQRExists" class="text-center flex-1 flex flex-col justify-center items-center">
            <!-- QR Code Image -->
            <div class="mb-8 flex justify-center">
              <div class="inline-block outline-2 outline-black rounded-xl p-6 bg-white shadow-lg">
                <img 
                  :src="qrCodeImageUrl" 
                  :alt="`QR Code for ${getSelectedVenueName()}`"
                  class="mx-auto block"
                  style="max-width: 250px; max-height: 250px;"
                />
              </div>
            </div>

            <!-- QR Action Buttons under QR Image -->
            <div class="flex flex-wrap gap-8 justify-center">
              <!-- Download Button -->
              <vs-button 
                @click="downloadQRCode" 
                color="primary"
                type="border"
                size="small"
                icon-pack="feather"
                icon="icon-download"
                class="px-6 py-2"
              >
                Download QR Code
              </vs-button>
              
              <!-- Copy Button -->
              <vs-button 
                @click="copyQRUrl" 
                color="success"
                type="border"
                size="small"
                icon-pack="feather"
                icon="icon-copy"
                class="px-6 py-2"
              >
                Copy URL
              </vs-button>

           
            </div>
          </div>

          <!-- No QR Code State -->
          <div v-else class="text-center flex-1 flex flex-col justify-center">
            <div class="text-gray-500">
              <feather-icon icon="QrCodeIcon" size="48" class="mx-auto mb-3 text-gray-300" />
              <p class="text-sm">No QR code generated yet</p>
              <p class="text-xs text-gray-400 mt-1">Click "Generate QR Code" to create one</p>
            </div>
          </div>
        </div>

        <!-- Actions Section -->
        <div class="bg-white border border-gray-200 rounded-lg p-6">
          <div class="flex flex-wrap gap-8 justify-center">
            <!-- Only show Generate button if no QR exists -->
            <vs-button 
              v-if="!selectedVenueQRExists"
              @click="generateQR" 
              :disabled="isGeneratingQR"
              color="primary"
              type="border"
              size="small"
              icon-pack="feather"
              icon="icon-plus"
            >
              {{ isGeneratingQR ? 'Generating...' : 'Generate QR Code' }}
            </vs-button>
            
            <!-- Only show Remove button if QR exists -->
            <vs-button 
              v-if="selectedVenueQRExists"
              @click="removeQR" 
              :disabled="isRemovingQR"
              color="danger"
              type="border"
              size="small"
              icon-pack="feather"
              icon="icon-trash"
            >
              {{ isRemovingQR ? 'Removing...' : 'Remove QR Code' }}
            </vs-button>

            <!-- Only show Update Settings button if QR exists -->
            <vs-button 
              v-if="selectedVenueQRExists"
              @click="openPortalSettings" 
              color="success"
              type="border"
              size="small"
              icon-pack="feather"
              icon="icon-edit"
            >
              Update Settings
            </vs-button>
          </div>
        </div>
      </div>

    </div>
  </vs-popup>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { _list as getVenues, _enableQuickBookQR, _removeQuickBookQR , _updateQuickBookSettings} from '@/services/QRBooker'
import { MAIN_DOMAIN } from '@/environment'

export default {
  name: 'QRBookerIndex',
  components: {
    'v-select': vSelect
  },
  data() {
    return {
      selectedVenue: null,
      venueOptions: [],
      companyID: this.$store.getters['company/getCompanyID'],
      isGeneratingQR: false,
      isRemovingQR: false,
      venueQRStatus: {}, // Track QR status for each venue
      // Portal settings
      portalConfig: {
        name: false,
        destination: false,
        phone: false,
        schedule_booking: false,
        allow_notes: false
      },
      provider: null,
      selectedCapabilities: [],
      isUpdatingPortal: false,
      showPortalModal: false,
      showQRBookerModal: false,
      availableCapabilities: null // Vehicle options from API
    }
  },
  computed: {
    // Determine if provider is infocab or autocab (default to infocab)
    isInfocab() {
      const p = this.$store.getters['auth/getAuthProvider']
      if (p) return String(p).toLowerCase() === 'infocab'
      return true
    },
    // Dynamic vehicle options based on provider type - use API data if available, otherwise fallback
    vehicleOptions() {
      // Use available_capabilities from API if available
      if (this.availableCapabilities && Array.isArray(this.availableCapabilities) && this.availableCapabilities.length > 0) {
        return this.availableCapabilities
      }
      
      // Fallback to hardcoded values if API data not available
      if (this.isInfocab) {
        // Infocab format
        return [
          {
            Featureid: 4,
            featureTypeId: 4,
            bitMask: 16,
            name: 'Saloon'
          },
          {
            Featureid: 18,
            featureTypeId: 4,
            bitMask: 262144,
            name: 'Standard'
          }
        ]
      } else {
        // Autocab format
        return [
        {
          id: 2,
          rowVersion: 4,
          shortCode: "S",
          name: "Saloon",
          requirement: "Vehicle",
          priority: 1,
          enabled: true,
          colour: {
            r: 225,
            g: 67,
            b: 67
          },
          excludeFromBroadcast: false,
          exclusiveCapability: false,
          operatorOverride: true
        },
        {
            id: 1,
            rowVersion: 1,
            shortCode: "6",
            name: "6 Seater",
            requirement: "Vehicle",
            priority: 0,
            enabled: true,
            colour: {
              r: 17,
              g: 185,
              b: 243
            },
            excludeFromBroadcast: false,
            exclusiveCapability: false,
            operatorOverride: true
          }
        ]
      }
    },
    // Track-by property based on provider type
    vehicleTrackBy() {
      return this.isInfocab ? 'Featureid' : 'id'
    },
    selectedVenueQRExists() {
      return this.selectedVenue && this.venueQRStatus[this.selectedVenue]
    },
    qrCodeImageUrl() {
      if (!this.selectedVenue) return ''
      const venue = this.venueOptions.find(v => v.id === this.selectedVenue)
      if (venue && venue.quickbook_qr && venue.quickbook_qr.qr_code_id) {
        return `${MAIN_DOMAIN}/quickbook/quickbook-${venue.quickbook_qr.qr_code_id}.svg`
      }
      // fallback mirrors the asset path used during existence checks (`checkQRStatus`)
      return `${MAIN_DOMAIN}/quickbook/venue-${this.selectedVenue}.svg`
    },
    qrCodeUrl() {
      if (!this.selectedVenue) return ''
      const venue = this.venueOptions.find(v => v.id === this.selectedVenue)
      if (venue && venue.quickbook_qr && venue.quickbook_qr.booking_url) {
        return `${venue.quickbook_qr.booking_url}`
      }
    },
    venuesWithQR() {
      // Count venues that have QR codes
      return this.venueOptions.filter(venue => this.venueQRStatus[venue.id]).length
    },
    venuesWithoutQR() {
      // Count venues that don't have QR codes
      return this.venueOptions.filter(venue => !this.venueQRStatus[venue.id]).length
    },
    totalVenues() {
      // Total number of venues
      return this.venueOptions.length
    }
  },
  watch: {
    '$store.state.company.companyID'(val) {
      this.companyID = val
      this.fetchVenues()
    },
    selectedVenue(newVenue) {
      // Only check QR status if the venue was selected by clicking on the tile, not by opening modal
      if (newVenue && !this.showQRBookerModal) {
        this.checkQRStatus(newVenue)
      }
    }
  },
  mounted() {
    this.fetchVenues()
  },
  methods: {
    // Ensure consistent config shape across UI and API
    parsePortalConfig(raw) {
      try {
        if (raw == null) return null
        return typeof raw === 'string' ? JSON.parse(raw) : { ...raw }
      } catch (e) {
        console.error('Failed to parse portal config', e)
        return null
      }
    },
    // For backend expecting an array (PHP), sending a plain object is decoded as an associative array
    serializePortalConfig(configObj) {
      return { ...(configObj || {}) }
    },
    fetchVenues() {
      if (!this.companyID) {
        console.warn('No company ID available')
        return
      }

      this.$vs.loading()
      getVenues(this.companyID)
        .then(response => {
          this.$vs.loading.close()
          this.venueOptions = response.data.obj.list || []
          
          // Extract available_capabilities from API response
          if (response.data.obj.available_capabilities) {
            // Handle if it's a JSON string or already an array
            if (typeof response.data.obj.available_capabilities === 'string') {
              try {
                this.availableCapabilities = JSON.parse(response.data.obj.available_capabilities)
              } catch (error) {
                console.error('Error parsing available_capabilities:', error)
                this.availableCapabilities = null
              }
            } else if (Array.isArray(response.data.obj.available_capabilities)) {
              this.availableCapabilities = response.data.obj.available_capabilities
            } else {
              this.availableCapabilities = null
            }
          } else {
            this.availableCapabilities = null
          }
          
          // Initialize QR status from backend data
          this.initializeVenueQRStatusFromBackend()
        })
        .catch(error => {
          this.$vs.loading.close()
          console.error('Error fetching venues:', error)
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to load venues',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    getSelectedVenueName() {
      if (!this.selectedVenue) return ''
      const venue = this.venueOptions.find(v => v.id === this.selectedVenue)
      return venue ? venue.display_name : 'Unknown Venue'
    },

    checkQRStatus(venueId) {
      // Check if QR code exists by trying to load the image
      const img = new Image()
      img.onload = () => {
        this.$set(this.venueQRStatus, venueId, true)
        console.log(`QR code found for venue ${venueId}`)
      }
      img.onerror = () => {
        this.$set(this.venueQRStatus, venueId, false)
        console.log(`No QR code found for venue ${venueId}`)
      }
      img.src = `${MAIN_DOMAIN}/quickbook/venue-${venueId}.svg`
    },

    checkAllQRStatus() {
      // Initialize all venues as having no QR code first
      this.venueOptions.forEach(venue => {
        this.$set(this.venueQRStatus, venue.id, false)
      })
      
      // Then check each venue for QR code existence
      this.venueOptions.forEach(venue => {
        this.checkQRStatus(venue.id)
      })
    },

    initializeVenueQRStatus() {
      // Initialize venue QR status object
      this.venueQRStatus = {}
      this.venueOptions.forEach(venue => {
        this.$set(this.venueQRStatus, venue.id, false)
      })
    },

    initializeVenueQRStatusFromBackend() {
      // Initialize venue QR status from backend data
      this.venueQRStatus = {}
      this.venueOptions.forEach(venue => {
        const hasQR = venue.quickbook_qr && venue.quickbook_qr.enabled && venue.quickbook_qr.status === 'active'
        this.$set(this.venueQRStatus, venue.id, hasQR)
      })
    },

    loadVenuePortalSettings(venueId) {
      // Load existing portal settings for a venue
      const venue = this.venueOptions.find(v => v.id === venueId)
      if (venue && venue.quickbook_qr && venue.quickbook_qr.config) {
        const parsed = this.parsePortalConfig(venue.quickbook_qr.config)
        this.portalConfig = parsed || {
          name: false,
          destination: false,
          phone: false,
          schedule_booking: false,
          allow_notes: false
        }
      } else {
        // Reset to default if no config exists
        this.portalConfig = {
          name: false,
          destination: false,
          phone: false,
          schedule_booking: false,
          allow_notes: false
        }
      }

      // Load capabilities - handle both infocab and autocab formats
      if (venue && venue.quickbook_qr && venue.quickbook_qr.capabilities) {
        try {
          const parsed = JSON.parse(venue.quickbook_qr.capabilities)
          // Normalize capabilities to match current vehicleOptions format
          this.selectedCapabilities = this.normalizeCapabilities(parsed)
        } catch (error) {
          console.error('Error parsing capabilities:', error)
          this.selectedCapabilities = []
        }
      } else {
        this.selectedCapabilities = []
      }
    },
    // Normalize capabilities from backend to match current vehicleOptions format
    normalizeCapabilities(capabilities) {
      if (!Array.isArray(capabilities) || capabilities.length === 0) {
        return []
      }
      
      // Check format of first capability to determine type
      const firstCap = capabilities[0]
      const isInfocabFormat = firstCap.hasOwnProperty('Featureid')
      
      // If capabilities are in different format than current vehicleOptions, try to match
      if (isInfocabFormat && !this.isInfocab) {
        // Convert infocab format to autocab format
        return capabilities.map(cap => {
          const match = this.vehicleOptions.find(v => 
            v.name.toLowerCase() === cap.name.toLowerCase()
          )
          return match || cap
        }).filter(cap => cap)
      } else if (!isInfocabFormat && this.isInfocab) {
        // Convert autocab format to infocab format
        return capabilities.map(cap => {
          const match = this.vehicleOptions.find(v => 
            v.name.toLowerCase() === (cap.name || '').toLowerCase()
          )
          return match || cap
        }).filter(cap => cap)
      }
      
      // If format matches, just filter to only include options in current vehicleOptions
      return capabilities.filter(cap => {
        const trackKey = this.isInfocab ? 'Featureid' : 'id'
        const capKey = this.isInfocab ? cap.Featureid : cap.id
        return this.vehicleOptions.some(v => v[trackKey] === capKey)
      })
    },

    generateQR() {
      if (!this.selectedVenue) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please select a venue first',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning'
        })
        return
      }

      this.isGeneratingQR = true
      const data = {
        venue_id: this.selectedVenue,
        duration: 15,
        // Use current portalConfig and add defaults if required by backend (send as plain object)
        config: { request_as: 'name', ...this.serializePortalConfig(this.portalConfig) },
        company_id: this.companyID
      }
      
      _enableQuickBookQR(data)
        .then(response => {
          this.isGeneratingQR = false
          this.updateVenueQRStatus(this.selectedVenue, true)
          // Refresh venue data to get updated QR information
          this.fetchVenues()
          this.$vs.notify({
            title: 'Success',
            text: 'QuickBook QR code enabled successfully',
            iconPack: 'feather',
            icon: 'icon-check-circle',
            color: 'success'
          })
        })
        .catch(error => {
          this.isGeneratingQR = false
          console.error('Error enabling QuickBook QR code:', error)
          this.$vs.notify({
            title: 'Error',
            text: (error.response && error.response.data && error.response.data.responseMessage) || 'Failed to enable QuickBook QR code',
            iconPack: 'feather',
            icon: 'icon-cross-circle',
            color: 'danger'
          })
        })
    },

    removeQR() {
      if (!this.selectedVenue) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please select a venue first',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning'
        })
        return
      }

      this.isRemovingQR = true
      const data = {
        venue_id: this.selectedVenue,
        company_id: this.companyID
      }
      
      _removeQuickBookQR(data)
        .then(response => {
          this.isRemovingQR = false
          this.updateVenueQRStatus(this.selectedVenue, false)
          // Refresh venue data to get updated QR information
          this.fetchVenues()
          this.$vs.notify({
            title: 'Success',
            text: 'QuickBook QR code disabled successfully',
            iconPack: 'feather',
            icon: 'icon-check-circle',
            color: 'success'
          })
        })
        .catch(error => {
          this.isRemovingQR = false
          console.error('Error disabling QuickBook QR code:', error)
          this.$vs.notify({
            title: 'Error',
            text: (error.response && error.response.data && error.response.data.responseMessage) || 'Failed to disable QuickBook QR code',
            iconPack: 'feather',
            icon: 'icon-cross-circle',
            color: 'danger'
          })
        })
    },


    getVenueQRStatus(venueId) {
      // Get QR status for a specific venue
      return this.venueQRStatus[venueId] || false
    },

    updateVenueQRStatus(venueId, hasQR) {
      // Update QR status for a specific venue
      this.$set(this.venueQRStatus, venueId, hasQR)
    },

    getVenueTileClass(venueId) {
      const isSelected = this.selectedVenue === venueId
      const hasQR = this.venueQRStatus[venueId]
      
      console.log(`Venue ${venueId}: selected=${isSelected}, hasQR=${hasQR}`)
      
      if (isSelected && hasQR) {
        return 'venue-tile-selected-with-qr'
      } else if (isSelected) {
        return 'venue-tile-selected'
      } else if (hasQR) {
        return 'venue-tile-qr-available'
      } else {
        return 'venue-tile-no-qr'
      }
    },

    openPortalSettings() {
      if (!this.selectedVenue) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please select a venue first',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning'
        })
        return
      }
      
      // Load existing portal settings for the selected venue
      this.loadVenuePortalSettings(this.selectedVenue)
      this.showPortalModal = true
    },

    closePortalModal() {
      this.showPortalModal = false
    },

    openQRBookerManagement(venueId = null) {
      // If venueId is provided, set it as selected venue
      if (venueId) {
        this.selectedVenue = venueId
      }
      
      if (!this.selectedVenue) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please select a venue first',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning'
        })
        return
      }
      
      this.showQRBookerModal = true
    },

    closeQRBookerModal() {
      this.showQRBookerModal = false
    },

    updatePortalSettings() {
      if (!this.selectedVenue) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please select a venue first',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning'
        })
        return
      }

      this.isUpdatingPortal = true

      // Prepare portal data (stringify config to ensure consistent backend type)
      const portalData = {
        venue_id: this.selectedVenue,
        // Send as plain object so backend receives an array/assoc
        config: this.serializePortalConfig(this.portalConfig),
        capabilities: JSON.stringify(this.selectedCapabilities),
        duration: 15
      }

      // For now, we'll use venue_id as portal_id since we don't have separate portal management
      // You may need to adjust this based on your backend API structure
      _updateQuickBookSettings(portalData)
        .then(response => {
          this.isUpdatingPortal = false
          this.showPortalModal = false
          // Refresh venue data to get updated settings
          this.fetchVenues()
          this.$vs.notify({
            title: response.data.responseTitle || 'Success',
            text: response.data.responseMessage || 'Portal settings updated successfully',
            iconPack: 'feather',
            icon: 'icon-check-circle',
            color: 'success'
          })
        })
        .catch(error => {
          this.isUpdatingPortal = false
          console.error('Error updating portal settings:', error)
          this.$vs.notify({
            title: 'Error',
            text: (error.response && error.response.data && error.response.data.responseMessage) || 'Failed to update portal settings',
            iconPack: 'feather',
            icon: 'icon-x-circle',
            color: 'danger'
          })
        })
    },

    copyQRUrl() {
      if (!this.qrCodeUrl) return

      const fallbackCopy = () => {
        const textArea = document.createElement('textarea')
        textArea.value = this.qrCodeUrl
        textArea.setAttribute('readonly', '')
        textArea.style.position = 'absolute'
        textArea.style.left = '-9999px'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$vs.notify({
          title: 'Copied',
          text: 'QR code URL copied to clipboard',
          iconPack: 'feather',
          icon: 'icon-check-circle',
          color: 'success'
        })
      }

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(this.qrCodeUrl).then(() => {
          this.$vs.notify({
            title: 'Copied',
            text: 'QR code URL copied to clipboard',
            iconPack: 'feather',
            icon: 'icon-check-circle',
            color: 'success'
          })
        }).catch(fallbackCopy)
      } else {
        fallbackCopy()
      }
    },

    downloadQRCode() {
      if (!this.selectedVenueQRExists) return
      
      const link = document.createElement('a')
      link.href = this.qrCodeImageUrl
      link.download = `qr-code-venue-${this.selectedVenue}.svg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      this.$vs.notify({
        title: 'Download Started',
        text: 'QR code download initiated',
        iconPack: 'feather',
        icon: 'icon-download',
        color: 'success'
      })
    },

    

    viewQRCode(venueId) {
      this.selectedVenue = venueId
      // Scroll to QR management section
      this.$nextTick(() => {
        const element = document.querySelector('.mt-6')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      })
    },

    openQRInNewTab() {
      if (!this.selectedVenueQRExists) return
      
      const qrUrl = this.qrCodeUrl
      window.open(qrUrl, '_blank')
      
      this.$vs.notify({
        title: 'Opening QR Code',
        text: 'QR code opened in new tab',
        iconPack: 'feather',
        icon: 'icon-external-link',
        color: 'info'
      })
    },

  }
}
</script>

<style scoped>
/* Static selection highlighting - no animations */

/* Default venue tile styling - white background with light grey */
.venue-tile-selected,
.venue-tile-selected-with-qr,
.venue-tile-qr-available,
.venue-tile-no-qr {
  background-color: white !important;
  border: 1px solid #e5e7eb !important; /* light grey border - reduced thickness */
}

/* Venue tile border styles */
.venue-tile-selected {
  border: 1px solid #059669 !important; /* green-600 */
}

.venue-tile-selected-with-qr {
  border: 1px solid #059669 !important; /* green-600 */
}

.venue-tile-qr-available {
  border: 1px solid #e5e7eb !important; /* light grey */
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

.venue-tile-qr-available:hover {
  border-color: #d1d5db !important; /* slightly darker grey on hover */
}

.venue-tile-no-qr {
  border: 1px solid #e5e7eb !important; /* light grey */
}

.venue-tile-no-qr:hover {
  border-color: #d1d5db !important; /* slightly darker grey on hover */
}
</style>
